import { getApi, postApi } from './api';

const API_URL = import.meta.env.VITE_API_URL as string;

export interface UserProfile {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  record_status: number;
  experience_level?: string;
  drinking_frequency?: string;
  wine_profile_type?: string; // This is what we're checking for
  typical_occasions?: string[];
  wine_type_preferences?: Record<string, number>;
  preferred_regions?: string[];
  loved_flavors?: Record<string, any>;
  disliked_flavors?: Record<string, any>;
  favorite_cuisines?: string[];
  dietary_restrictions?: string[];
  preferred_pairing_styles?: string[];
}

/**
 * Fetches the user's complete profile from the API
 * @returns Promise<UserProfile | null> - Returns the user profile or null if failed
 */
export async function getUserProfile(): Promise<UserProfile | null> {
  try {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      console.error("No auth token found");
      return null;
    }

    const response = await getApi(API_URL, "profile", idToken);
    
    // The getUserProfile Lambda returns data in "data" property when successful
    if (response && response.success && response.data) {
      return response.data as UserProfile;
    } else if (response && !response.success) {
      console.error("API returned error:", response.message || response.error);
      return null;
    } else if (response) {
      // Fallback: try to use response directly
      return response as UserProfile;
    }
    
    return null;
  } catch (error) {
    console.error("Failed to fetch user profile:", error);
    return null;
  }
}

/**
 * Checks if the user has completed their wine profile (has wine_profile_type set)
 * @returns Promise<boolean> - Returns true if profile is complete, false otherwise
 */
export async function isProfileComplete(): Promise<boolean> {
  try {
    console.log("Checking profile completeness...");
    const profile = await getUserProfile();
    
    if (!profile) {
      console.log("No profile data found - profile incomplete");
      return false;
    }

    // Check if wine_profile_type exists and is not null/empty
    const hasWineProfileType = Boolean(profile.wine_profile_type && 
                              profile.wine_profile_type.trim().length > 0);
    
    console.log("Profile completeness check:", {
      email: profile.email,
      wine_profile_type: profile.wine_profile_type,
      isComplete: hasWineProfileType
    });

    return hasWineProfileType;
  } catch (error) {
    console.error("Error checking profile completeness:", error);
    return false;
  }
}

/**
 * Submits the user's completed profile to the API
 * @param userProfile - The user profile data from the profiler wizard
 * @returns Promise<any> - Returns the wine profile type data if successful, null otherwise
 */
export async function submitUserProfile(userProfile: any): Promise<any> {
  try {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      console.error("No auth token found");
      return false;
    }

    // Transform frontend UserProfile to backend API format (without wine_profile_type - let backend determine it)
    const apiPayload = transformProfileForAPI(userProfile, null);
    
    console.log("Submitting user profile:", apiPayload);

    const response = await postApi(API_URL, "profile", apiPayload, idToken);
    
    // Check if the response indicates success
    const responseData = JSON.parse(response as string);
    console.log("API Response:", responseData);
    
    if (responseData && responseData.success && responseData.data) {
      console.log("Profile submitted successfully");
      console.log("Wine profile type data:", responseData.data.wine_profile_type);
      return responseData.data.wine_profile_type;
    } else {
      console.error("Profile submission failed:", responseData);
      return null;
    }
  } catch (error) {
    console.error("Failed to submit user profile:", error);
    return null;
  }
}

/**
 * Transforms the frontend UserProfile structure to match the backend API format
 * @param userProfile - Frontend UserProfile from profiler wizard
 * @param wineProfileType - The determined wine profile type (optional, backend will determine if null)
 * @returns Transformed profile data for API submission
 */
function transformProfileForAPI(userProfile: any, wineProfileType: string | null) {
  // Transform loved flavors from frontend to backend format
  const transformLovedFlavors = (flavors: any[]) => {
    const categorized: Record<string, Array<{ descriptor: string; intensity: number }>> = {};
    
    flavors.forEach(flavor => {
      if (!categorized[flavor.category]) {
        categorized[flavor.category] = [];
      }
      categorized[flavor.category].push({
        descriptor: flavor.descriptor,
        intensity: flavor.intensity
      });
    });
    
    return categorized;
  };

  // Transform disliked flavors from frontend to backend format
  const transformDislikedFlavors = (flavors: any[]) => {
    const categorized: Record<string, Array<{ descriptor: string; aversion_level: number }>> = {};
    
    flavors.forEach(flavor => {
      if (!categorized[flavor.category]) {
        categorized[flavor.category] = [];
      }
      categorized[flavor.category].push({
        descriptor: flavor.descriptor,
        aversion_level: flavor.intensity // Using intensity as aversion_level
      });
    });
    
    return categorized;
  };

  // Transform wine type preferences from frontend array to backend object
  const transformWineTypePreferences = (wineTypes: any[]) => {
    const preferences: Record<string, number> = {};
    wineTypes.forEach(wineType => {
      preferences[wineType.wine_color] = wineType.preference_level;
    });
    return preferences;
  };

  return {
    // Basic wine experience
    experience_level: userProfile.wine.experience,
    
    // Drinking habits from wine profile
    drinking_frequency: userProfile.wine.drinking_habits?.frequency,
    everyday_wine_min_budget: userProfile.wine.drinking_habits?.everyday_wine_min_budget,
    everyday_wine_max_budget: userProfile.wine.drinking_habits?.everyday_wine_max_budget,
    special_occasion_min_budget: userProfile.wine.drinking_habits?.special_occasion_min_budget,
    special_occasion_max_budget: userProfile.wine.drinking_habits?.special_occasion_max_budget,
    budget_currency: userProfile.wine.drinking_habits?.budget_currency || 'usd',
    
    // Typical occasions
    typical_occasions: userProfile.wine.occasions,
    
    // Wine type preferences
    wine_type_preferences: userProfile.wine.wine_types ? transformWineTypePreferences(userProfile.wine.wine_types) : undefined,
    
    // Preferred regions
    preferred_regions: userProfile.wine.preferred_regions,
    
    // Taste preferences from taste profile
    taste_preferences: {
      sweetness_preferred_level: userProfile.taste.sweetness_preferred_level,
      sweetness_tolerance_min: userProfile.taste.sweetness_tolerance_min,
      sweetness_tolerance_max: userProfile.taste.sweetness_tolerance_max,
      acidity_preferred_level: userProfile.taste.acidity_preferred_level,
      acidity_tolerance_min: userProfile.taste.acidity_tolerance_min,
      acidity_tolerance_max: userProfile.taste.acidity_tolerance_max,
      tannin_preferred_level: userProfile.taste.tannin_preferred_level,
      body_preference: userProfile.taste.body_preference,
      alcohol_preferred_min: userProfile.taste.alcohol_preferred_min,
      alcohol_preferred_max: userProfile.taste.alcohol_preferred_max,
      alcohol_sensitivity: userProfile.taste.alcohol_sensitivity,
    },
    
    // Loved flavors
    loved_flavors: userProfile.wine.loved_flavors ? transformLovedFlavors(userProfile.wine.loved_flavors) : undefined,
    
    // Disliked flavors
    disliked_flavors: userProfile.wine.disliked_flavors ? transformDislikedFlavors(userProfile.wine.disliked_flavors) : undefined,
    
    // Favorite cuisines from taste preferences
    favorite_cuisines: userProfile.taste.cuisines,
    
    // Dietary restrictions
    dietary_restrictions: userProfile.wine.dietary_restrictions,
    
    // Preferred pairing styles
    preferred_pairing_styles: userProfile.wine.pairing_styles,
    
    // Wine discovery
    openness_to_new_wines: userProfile.wine.openness_to_new_wines,
    
    // Wine profile type (let backend determine if null)
    ...(wineProfileType && { wine_profile_type: wineProfileType }),
  };
}